import { buildAiAnnotationContext, type BuildAiAnnotationContextInput } from "@/ai/context/buildContext";
import { audioEngine } from "@/ai/engines/audioEngine";
import { continuityEngine } from "@/ai/engines/continuityEngine";
import type { AiProposalEngine } from "@/ai/engines/engine.types";
import { intentEngine } from "@/ai/engines/intentEngine";
import { npcEngine } from "@/ai/engines/npcEngine";
import { timingEngine } from "@/ai/engines/timingEngine";
import { normalizeAiAnnotation } from "@/ai/normalizer/normalizeAnnotation";
import type { TimelineAnnotation, TimelineAction } from "@/state/timeline/timeline.types";

const DEFAULT_ENGINES: AiProposalEngine[] = [
  continuityEngine,
  timingEngine,
  audioEngine,
  npcEngine,
  intentEngine,
];

export const OMNICINE_AI_ANNOTATIONS_EMITTED_EVENT = "omnicine:annotation:ai-proposals:emitted";
export const OMNICINE_AI_ANNOTATION_SUGGESTIONS_EVENT = "omnicine:annotation:ai-suggestions:queued";

export type EmitAiAnnotationsOptions = {
  dispatch: (action: TimelineAction) => void;
  timelineState?: { viewport?: { startSec?: number; endSec?: number } | null } | null;
  transactionId?: string;
  note?: string;
};

export type QueueAiSuggestionsOptions = {
  openSurface?: boolean;
  source?: "inspector" | "panel" | "auto";
  replaceExisting?: boolean;
};

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function runAiProposalBridge(input: BuildAiAnnotationContextInput): TimelineAnnotation[] {
  const context = buildAiAnnotationContext(input);
  const seenKeys = new Set(context.existingAnnotationKeys);
  const annotations: TimelineAnnotation[] = [];

  for (const engine of DEFAULT_ENGINES) {
    for (const draft of engine(context)) {
      const annotation = normalizeAiAnnotation(draft, context, seenKeys);
      if (annotation) annotations.push(annotation);
    }
  }

  return annotations.sort((left, right) => (left.timeSec ?? 0) - (right.timeSec ?? 0));
}

export function emitAiAnnotationsToTimeline(
  annotations: TimelineAnnotation[],
  options: EmitAiAnnotationsOptions,
): TimelineAnnotation[] {
  if (!annotations.length) return [];

  const transactionId = options.transactionId ?? `txn-ai-${Date.now().toString(36)}`;

  for (const annotation of annotations) {
    options.dispatch({
      type: "annotation/add",
      payload: { annotation },
      meta: {
        source: "ai-assist",
        transactionId,
        note: options.note ?? "ai proposal bridge emission",
      },
    } as TimelineAction);

    options.dispatch({
      type: "marker/add",
      payload: {
        marker: {
          id: `marker-${annotation.id}`,
          timeSec: annotation.timeSec ?? 0,
          label: annotation.summary ?? annotation.rawNote,
          source: "ai-assist",
          annotation: {
            origin: "annotation_surface",
            annotationId: annotation.id,
            type: annotation.type,
            subtype: annotation.subtype ?? null,
            severity: annotation.severity ?? null,
            summary: annotation.summary ?? annotation.rawNote,
            state: "published",
            source: "ai",
            assetLabel: annotation.assetLabel,
            clipId: annotation.clipId,
            linkedAudio: annotation.linkedAudio,
            frameIndex: annotation.frameIndex,
            anchorTimecode: annotation.anchorTimecode,
            rawNote: annotation.rawNote,
            createdAt: annotation.createdAt,
          },
        },
      },
      meta: {
        source: "ai-assist",
        transactionId,
        note: options.note ?? "ai proposal bridge marker emission",
      },
    } as TimelineAction);
  }

  const firstTimeSec = annotations
    .map((annotation) => finiteNumber(annotation.timeSec))
    .find((value): value is number => value != null);

  const viewportStartSec = finiteNumber(options.timelineState?.viewport?.startSec);
  const viewportEndSec = finiteNumber(options.timelineState?.viewport?.endSec);

  if (firstTimeSec != null) {
    options.dispatch({
      type: "timeline/setPlayhead",
      payload: { timeSec: firstTimeSec },
      meta: {
        source: "ai-assist",
        transactionId,
        note: options.note ?? "ai proposal bridge playhead focus",
      },
    } as TimelineAction);

    if (viewportStartSec != null && viewportEndSec != null) {
      const spanSec = Math.max(0.5, viewportEndSec - viewportStartSec);
      const isVisible = firstTimeSec >= viewportStartSec && firstTimeSec <= viewportEndSec;

      if (!isVisible) {
        const halfSpan = spanSec / 2;
        const startSec = Math.max(0, firstTimeSec - halfSpan);
        const endSec = Math.max(startSec + 0.5, startSec + spanSec);

        options.dispatch({
          type: "timeline/setViewport",
          payload: { startSec, endSec },
          meta: {
            source: "ai-assist",
            transactionId,
            note: options.note ?? "ai proposal bridge viewport focus",
          },
        } as TimelineAction);
      }
    }
  }

  window.dispatchEvent(new CustomEvent(OMNICINE_AI_ANNOTATIONS_EMITTED_EVENT, {
    detail: {
      count: annotations.length,
      transactionId,
      annotationIds: annotations.map((annotation) => annotation.id),
      nodeIds: annotations.map((annotation) => annotation.nodeId).filter(Boolean),
    },
  }));

  return annotations;
}

export function runAndEmitAiProposalBridge(
  input: BuildAiAnnotationContextInput,
  options: EmitAiAnnotationsOptions,
): TimelineAnnotation[] {
  return emitAiAnnotationsToTimeline(runAiProposalBridge(input), options);
}

export function queueAiAnnotationSuggestions(
  annotations: TimelineAnnotation[],
  options: QueueAiSuggestionsOptions = {},
): TimelineAnnotation[] {
  if (!annotations.length) return [];

  window.dispatchEvent(new CustomEvent(OMNICINE_AI_ANNOTATION_SUGGESTIONS_EVENT, {
    detail: {
      suggestions: annotations,
      openSurface: options.openSurface ?? true,
      source: options.source ?? "inspector",
      replaceExisting: !!options.replaceExisting,
      queuedAt: Date.now(),
    },
  }));

  return annotations;
}